/** Shared date/location copy for home exhibition UI */

export function parseIsoLocal(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

export function formatExhibitionDateRange(start, end) {
  const opts = { month: 'short', day: 'numeric', year: 'numeric' };
  const s = parseIsoLocal(start);
  if (!s) return start;
  if (!end || end === start) {
    return s.toLocaleDateString(undefined, { ...opts, weekday: 'long' });
  }
  const e = parseIsoLocal(end);
  if (!e) return s.toLocaleDateString(undefined, opts);
  return `${s.toLocaleDateString(undefined, opts)} – ${e.toLocaleDateString(undefined, opts)}`;
}

/** Compact one-line dates for the top banner, e.g. "28–29 May 2026" */
export function formatExhibitionBannerDates(start, end) {
  const s = parseIsoLocal(start);
  if (!s) return String(start || '');
  const mon = s.toLocaleDateString(undefined, { month: 'short' });
  const yr = s.getFullYear();
  const d1 = s.getDate();
  if (!end || end === start) {
    return `${d1} ${mon} ${yr}`;
  }
  const e = parseIsoLocal(end);
  if (!e) return `${d1} ${mon} ${yr}`;
  const d2 = e.getDate();
  const mon2 = e.toLocaleDateString(undefined, { month: 'short' });
  const yr2 = e.getFullYear();
  if (mon === mon2 && yr === yr2) {
    return `${d1}–${d2} ${mon} ${yr}`;
  }
  return `${d1} ${mon} ${yr} – ${d2} ${mon2} ${yr2}`;
}

/** Date pill for compact schedule rows */
export function formatScheduleDatePill(start, end) {
  const s = parseIsoLocal(start);
  if (!s) return { month: '', day: '', year: String(start || '') };
  const month = s.toLocaleDateString(undefined, { month: 'short' });
  const year = String(s.getFullYear());
  let day = String(s.getDate());
  if (end && end !== start) {
    const e = parseIsoLocal(end);
    if (e) day = `${s.getDate()}–${e.getDate()}`;
  }
  return { month, day, year };
}

/** Venue + city/country without repeating the city when it is already in the venue line. */
export function formatExhibitionWhereLine(row) {
  const venue = String(row?.venue || '').trim();
  const city = String(row?.city || '').trim();
  const country = String(row?.country || '').trim();
  if (!venue) {
    return [city, country].filter(Boolean).join(', ');
  }
  const cityInVenue = city && venue.toLowerCase().includes(city.toLowerCase());
  const suffix = cityInVenue ? country : [city, country].filter(Boolean).join(', ');
  return [venue, suffix].filter(Boolean).join(' · ');
}

export function groupExhibitionsByMonth(items) {
  const map = new Map();
  for (const row of items) {
    const d = parseIsoLocal(row.event_start);
    const key = d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : 'unknown';
    const label = d
      ? d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
      : 'Other dates';
    if (!map.has(key)) {
      map.set(key, { key, label, items: [] });
    }
    map.get(key).items.push(row);
  }
  return [...map.values()].sort((a, b) => a.key.localeCompare(b.key));
}

export function normalizeLocationLabel(value) {
  return String(value || '').trim().toLowerCase();
}

export function exhibitionMatchesFilter(row, { query, city, country }) {
  if (city) {
    if (normalizeLocationLabel(row.city) !== normalizeLocationLabel(city)) return false;
  }
  if (country) {
    if (normalizeLocationLabel(row.country) !== normalizeLocationLabel(country)) return false;
  }
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const hay = [row.title, row.venue, row.city, row.country, row.details]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

export function uniqueCitiesFromExhibitions(items) {
  const seen = new Map();
  for (const row of items) {
    const city = String(row.city || '').trim();
    if (city && !seen.has(city.toLowerCase())) seen.set(city.toLowerCase(), city);
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b));
}

export function uniqueCountriesFromExhibitions(items) {
  const seen = new Map();
  for (const row of items) {
    const country = String(row.country || '').trim();
    if (country && !seen.has(country.toLowerCase())) seen.set(country.toLowerCase(), country);
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b));
}

/** Cities/countries available for chip rows given sibling filters (not the same dimension). */
export function exhibitionItemsForCityChips(items, { country = '', query = '' } = {}) {
  return items.filter((row) => {
    if (country && normalizeLocationLabel(row.country) !== normalizeLocationLabel(country)) return false;
    if (String(query || '').trim() && !exhibitionMatchesFilter(row, { query, city: '', country: '' })) return false;
    return true;
  });
}

/** Countries for chip row — search only (never narrow by selected city). */
export function exhibitionItemsForCountryChips(items, { query = '' } = {}) {
  if (!String(query || '').trim()) return items;
  return items.filter((row) => exhibitionMatchesFilter(row, { query, city: '', country: '' }));
}

export function exhibitionCountByCity(items) {
  const counts = new Map();
  for (const row of items) {
    const city = String(row.city || '').trim();
    if (!city) continue;
    const key = normalizeLocationLabel(city);
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return counts;
}

/** Whether a show is live today or still upcoming (null if past / unknown). */
export function exhibitionLiveStatus(row) {
  const start = parseIsoLocal(row?.event_start);
  if (!start) return null;
  const end = parseIsoLocal(row?.event_end || row?.event_start) || start;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const startDay = new Date(start);
  startDay.setHours(0, 0, 0, 0);
  const endDay = new Date(end);
  endDay.setHours(0, 0, 0, 0);
  if (today >= startDay && today <= endDay) return 'live';
  if (today < startDay) return 'upcoming';
  return null;
}

export function exhibitionStatusLabel(status) {
  if (status === 'live') return 'Happening now';
  if (status === 'upcoming') return 'Coming up';
  return '';
}

/** Upcoming/current shows offer RSVP by default when no per-show flags are set. */
export function exhibitionWithDefaultRsvp(row) {
  if (!row || row.rsvp_enabled || row.preorder_enabled) return row;
  const status = exhibitionLiveStatus(row);
  if (status === 'upcoming' || status === 'live') {
    return { ...row, rsvp_enabled: true };
  }
  return row;
}

/** Single-line location string for maps / calendar. */
export function exhibitionLocationLabel(row) {
  return [row?.venue, row?.city, row?.country].filter(Boolean).join(', ');
}

export function exhibitionGoogleMapsUrl(row) {
  const q = exhibitionLocationLabel(row).trim();
  if (!q) return '';
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

function googleCalendarEndExclusive(iso) {
  const d = parseIsoLocal(iso);
  if (!d) return iso?.replace(/-/g, '') || '';
  d.setDate(d.getDate() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}${m}${day}`;
}

/** All-day event link for Google Calendar. */
export function exhibitionGoogleCalendarUrl(row, { brandName = 'Saloni Designs' } = {}) {
  const start = String(row?.event_start || '').replace(/-/g, '');
  if (!start) return '';
  const end = googleCalendarEndExclusive(row?.event_end || row?.event_start);
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: String(row?.title || `${brandName} exhibition`).trim(),
    dates: `${start}/${end}`,
  });
  const location = exhibitionLocationLabel(row);
  if (location) params.set('location', location);
  const details = [row?.details, row?.external_url].filter(Boolean).join('\n\n');
  if (details) params.set('details', details);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/** JSON-LD ItemList of upcoming Event entries for /exhibitions SEO. */
export function exhibitionsScheduleJsonLd(upcoming, { brandName = 'Saloni Designs', limit = 20 } = {}) {
  if (!Array.isArray(upcoming) || upcoming.length === 0) return null;

  const itemListElement = upcoming.slice(0, limit).map((row, i) => {
    const event = {
      '@type': 'Event',
      name: String(row?.title || `${brandName} exhibition`).trim(),
      startDate: row?.event_start,
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    };
    if (row?.event_end && row.event_end !== row.event_start) {
      event.endDate = row.event_end;
    }
    const locationLabel = exhibitionLocationLabel(row);
    if (locationLabel) {
      event.location = {
        '@type': 'Place',
        name: row?.venue?.trim() || locationLabel,
        address: {
          '@type': 'PostalAddress',
          ...(row?.city ? { addressLocality: row.city } : {}),
          ...(row?.country ? { addressCountry: row.country } : {}),
        },
      };
    }
    const externalUrl = String(row?.external_url || '').trim();
    if (externalUrl) event.url = externalUrl;

    return {
      '@type': 'ListItem',
      position: i + 1,
      item: event,
    };
  });

  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `${brandName} exhibition schedule`,
    itemListElement,
  });
}
